import axios from 'axios';
import { TWILIO_API_URL } from '../config/twilio';
import { apiConfig } from '../shared/config/api';
import { isAgentPhoneWindow } from './agentPhone/agentPhoneBridge';
import { webRTCCallService } from './WebRTCCallService';

export type CallPreference = 'browser' | 'pstn';
export type WorkerAvailabilityStatus = 'idle' | 'offline' | 'busy' | 'stale';

export interface AgentVoiceBootstrapResult {
  workerStatus: WorkerAvailabilityStatus;
  callPreference: CallPreference;
  deviceReady: boolean;
  initialized: boolean;
  skipped?: boolean;
  error?: string;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function fetchCallPreference(userId: string, token: string): Promise<CallPreference> {
  try {
    const userResponse = await axios.get(`${apiConfig.baseUrl}/user/${userId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return userResponse.data?.twilio?.voice?.callPreference === 'pstn' ? 'pstn' : 'browser';
  } catch (error) {
    console.warn('[VoiceBootstrap] Failed to fetch call preference, defaulting to browser:', error);
    return 'browser';
  }
}

/**
 * When the worker is idle and uses browser calling, ensure Twilio Device + socket presence
 * are ready. Safe to call multiple times (e.g. on login, focus, reconnect).
 */
export async function bootstrapAgentVoiceIfNeeded(
  userId: string,
  agentName: string,
  options?: { maxAttempts?: number },
): Promise<AgentVoiceBootstrapResult> {
  const maxAttempts = options?.maxAttempts ?? 3;
  const authToken = localStorage.getItem('token');

  if (!authToken) {
    return {
      workerStatus: 'offline',
      callPreference: 'browser',
      deviceReady: false,
      initialized: false,
      skipped: true,
      error: 'Not authenticated',
    };
  }

  let workerStatus: WorkerAvailabilityStatus = 'offline';
  let callPreference: CallPreference = 'browser';

  try {
    const statusResponse = await axios.get(`${TWILIO_API_URL}/workers/${userId}/info`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    workerStatus = statusResponse.data.worker?.status || 'offline';
    callPreference = await fetchCallPreference(userId, authToken);
  } catch (error) {
    console.error('[VoiceBootstrap] Failed to load worker status:', error);
    return {
      workerStatus: 'offline',
      callPreference: 'browser',
      deviceReady: webRTCCallService.isDeviceReady(),
      initialized: false,
      error: 'Failed to load agent status',
    };
  }

  if (workerStatus !== 'idle' || callPreference !== 'browser') {
    return {
      workerStatus,
      callPreference,
      deviceReady: webRTCCallService.isDeviceReady(),
      initialized: false,
      skipped: true,
    };
  }

  if (webRTCCallService.isDeviceReady()) {
    webRTCCallService.ensureSocketAndAgentIdentified(userId, agentName);
    return {
      workerStatus,
      callPreference,
      deviceReady: true,
      initialized: true,
    };
  }

  let lastError: unknown;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      console.log(`[VoiceBootstrap] Initializing voice (attempt ${attempt}/${maxAttempts})…`);
      await webRTCCallService.ensureReadyForCalls(
        userId,
        agentName,
        undefined,
        isAgentPhoneWindow() ? 'phone' : 'web',
      );
      return {
        workerStatus,
        callPreference,
        deviceReady: webRTCCallService.isDeviceReady(),
        initialized: true,
      };
    } catch (error) {
      lastError = error;
      console.error(`[VoiceBootstrap] Attempt ${attempt} failed:`, error);
      if (attempt < maxAttempts) {
        await sleep(800 * attempt);
      }
    }
  }

  const message =
    lastError instanceof Error ? lastError.message : 'Voice device failed to initialize';

  return {
    workerStatus,
    callPreference,
    deviceReady: webRTCCallService.isDeviceReady(),
    initialized: false,
    error: message,
  };
}
